const API_BASE = import.meta.env.VITE_API_URL || '/api'

function authHeaders() {
  const token = localStorage.getItem('fw_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function fetchFlights(bbox, radius) {
  const params = new URLSearchParams()
  if (bbox) params.set('bbox', bbox)
  if (radius) {
    params.set('lat', radius.lat)
    params.set('lon', radius.lon)
    params.set('radius', radius.km)
  }
  
  try {
    const res = await fetch(`${API_BASE}/flights?${params}`, {
      headers: { 'Accept': 'application/json' }
    })
    if (!res.ok) throw new Error('Flight data unavailable')
    return await res.json()
  } catch (err) {
    console.warn('API fallback to mock data:', err.message)
    return null
  }
}

export async function fetchWeather(icao) {
  try {
    const res = await fetch(`${API_BASE}/weather?icao=${icao}`)
    if (!res.ok) throw new Error('Weather unavailable')
    return await res.json()
  } catch (err) {
    return null
  }
}

export async function fetchSchedules(airport, type = 'departure') {
  try {
    const res = await fetch(`${API_BASE}/schedules?airport=${airport}&type=${type}`)
    if (!res.ok) throw new Error('Schedules unavailable')
    return await res.json()
  } catch (err) {
    return null
  }
}

// Auth API
export async function requestMagicLink(email, next) {
  const res = await fetch(`${API_BASE}/auth/magic-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, next }),
  })
  return res.json()
}

export async function verifyToken(token) {
  const res = await fetch(`${API_BASE}/auth/verify?token=${token}`)
  return res.json()
}

export async function loginWithPassword(email, password, totpCode) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, totpCode }),
  })
  return res.json()
}

// Displays (customer-owned)
export async function getMyDisplays() {
  const res = await fetch(`${API_BASE}/displays`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function createDisplay(display) {
  const res = await fetch(`${API_BASE}/displays`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(display),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to create display')
  return data
}

export async function claimDevice(payload) {
  const res = await fetch(`${API_BASE}/devices/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to pair device')
  return data
}

export async function updateDisplay(id, display) {
  const res = await fetch(`${API_BASE}/displays/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(display),
  })
  return res.json()
}

export async function deleteDisplay(id) {
  const res = await fetch(`${API_BASE}/displays/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return res.json()
}

export async function getPlans() {
  const res = await fetch(`${API_BASE}/plans`)
  if (!res.ok) throw new Error('Plans unavailable')
  return res.json()
}

export async function createCheckout(planId, billingInterval) {
  const res = await fetch(`${API_BASE}/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ planId, billingInterval }),
  })
  if (!res.ok) throw new Error('Checkout failed')
  return res.json()
}

export async function getMe() {
  const res = await fetch(`${API_BASE}/me`, { headers: authHeaders() })
  if (!res.ok) return null
  return res.json()
}

export async function logout() {
  await fetch(`${API_BASE}/auth/logout`, { method: 'POST', headers: authHeaders() })
}

// Admin API
export async function getAdminStats() {
  const res = await fetch(`${API_BASE}/admin/stats`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function getAdminActivity() {
  const res = await fetch(`${API_BASE}/admin/activity`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function getAdminUsers() {
  const res = await fetch(`${API_BASE}/admin/users`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function getAdminSubscriptions() {
  const res = await fetch(`${API_BASE}/admin/subscriptions`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function getAdminDisplays() {
  const res = await fetch(`${API_BASE}/admin/displays`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function getAdminPlans() {
  const res = await fetch(`${API_BASE}/admin/plans`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function createPlan(plan) {
  const res = await fetch(`${API_BASE}/admin/plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(plan),
  })
  return res.json()
}

export async function updatePlan(id, plan) {
  const res = await fetch(`${API_BASE}/admin/plans/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(plan),
  })
  return res.json()
}

export async function deletePlan(id) {
  const res = await fetch(`${API_BASE}/admin/plans/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return res.json()
}

export async function getAdminStripeSettings() {
  const res = await fetch(`${API_BASE}/admin/stripe-settings`, { headers: authHeaders() })
  if (!res.ok) throw new Error('Unauthorized')
  return res.json()
}

export async function updateStripeSettings(payload) {
  const res = await fetch(`${API_BASE}/admin/stripe-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  })
  return res.json()
}
